import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  LogOut, 
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  Clock,
  Car,
  Trash2,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
  RefreshCw,
  Loader2,
  ChevronDown,
  Send
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  useEffect(() => {
    // Check if admin is logged in
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [bookingsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers })
      ]);
      
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        sessionStorage.removeItem('adminToken');
        navigate('/admin');
      }
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    setIsUpdating(true);
    try {
      const token = sessionStorage.getItem('adminToken');
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBookings(prev => 
        prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b)
      );
      toast.success(`Booking marked as ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      const token = sessionStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBookings(prev => prev.filter(b => b.id !== bookingId));
      toast.success('Booking deleted');
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const token = sessionStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContacts(prev => prev.filter(c => c.id !== contactId));
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete message');
    }
  };

  const sendTestEmail = async () => {
    setIsSendingTest(true);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${API}/admin/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setIsSendingTest(false);
    }
  };

  const filteredBookings = statusFilter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === statusFilter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400';
      case 'confirmed': return 'bg-blue-500/20 text-blue-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  const tripTypeLabels = {
    airport_transfer: 'Airport Transfer',
    hourly: 'Hourly Service',
    city_to_city: 'City to City',
    event: 'Event'
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-serif text-white">Admin Dashboard</h1>
            <p className="text-sm text-[#A3A3A3]">
              Capital Chauffeur Services
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="send-test-email-btn"
              onClick={sendTestEmail}
              disabled={isSendingTest}
              className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none text-xs"
            >
              {isSendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {isSendingTest ? 'Sending...' : 'Send Test Email'}
            </Button>
            <span className="text-[#A3A3A3] text-sm">
              {sessionStorage.getItem('adminUser')}
            </span>
            <Button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 rounded-none"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="text-3xl font-serif text-[#D4AF37] mb-2">
              {bookings.length}
            </div>
            <div className="text-[#A3A3A3] text-sm">Total Bookings</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="text-3xl font-serif text-yellow-400 mb-2">
              {bookings.filter(b => b.status === 'pending').length}
            </div>
            <div className="text-[#A3A3A3] text-sm">Pending</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="text-3xl font-serif text-green-400 mb-2">
              {bookings.filter(b => b.status === 'completed').length}
            </div>
            <div className="text-[#A3A3A3] text-sm">Completed</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="text-3xl font-serif text-blue-400 mb-2">
              {contacts.length}
            </div>
            <div className="text-[#A3A3A3] text-sm">Contact Messages</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6 border-b border-white/10">
          <button
            data-testid="tab-bookings"
            onClick={() => setActiveTab('bookings')}
            className={`pb-4 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'bookings'
                ? 'border-[#D4AF37] text-[#D4AF37]'
                : 'border-transparent text-[#A3A3A3] hover:text-white'
            }`}
          >
            Booking Requests ({bookings.length})
          </button>
          <button
            data-testid="tab-contacts"
            onClick={() => setActiveTab('contacts')}
            className={`pb-4 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'contacts'
                ? 'border-[#D4AF37] text-[#D4AF37]'
                : 'border-transparent text-[#A3A3A3] hover:text-white'
            }`}
          >
            Contact Messages ({contacts.length})
          </button>
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            {/* Filters */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-[#A3A3A3]" />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40 bg-transparent border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="all" className="text-white">All Status</SelectItem>
                      <SelectItem value="pending" className="text-white">Pending</SelectItem>
                      <SelectItem value="confirmed" className="text-white">Confirmed</SelectItem>
                      <SelectItem value="completed" className="text-white">Completed</SelectItem>
                      <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={fetchData}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 rounded-none"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* Bookings Table */}
            <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Client</th>
                      <th className="text-left p-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Service</th>
                      <th className="text-left p-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Date</th>
                      <th className="text-left p-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Status</th>
                      <th className="text-right p-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-[#A3A3A3]">
                          No bookings found
                        </td>
                      </tr>
                    ) : (
                      filteredBookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-4">
                            <div>
                              <div className="text-white font-medium">{booking.full_name}</div>
                              <div className="text-[#A3A3A3] text-sm">{booking.email}</div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-white">{tripTypeLabels[booking.trip_type] || booking.trip_type}</div>
                            <div className="text-[#A3A3A3] text-sm">{booking.vehicle_type}</div>
                          </td>
                          <td className="p-4">
                            <div className="text-white">{booking.pickup_date}</div>
                            <div className="text-[#A3A3A3] text-sm">{booking.pickup_time}</div>
                          </td>
                          <td className="p-4">
                            <span className={`px-3 py-1 text-xs font-medium uppercase ${getStatusColor(booking.status)}`}>
                              {booking.status}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                onClick={() => setSelectedBooking(booking)}
                                variant="ghost"
                                size="sm"
                                className="text-[#D4AF37] hover:bg-[#D4AF37]/10"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => deleteBooking(booking.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:bg-red-400/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-white/10 p-8 text-center">
                <p className="text-[#A3A3A3]">No contact messages</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div key={contact.id} className="bg-[#0A0A0A] border border-white/10 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-white font-medium">{contact.name}</h3>
                      <div className="flex items-center gap-4 mt-1">
                        <a href={`mailto:${contact.email}`} className="text-[#D4AF37] text-sm hover:underline">
                          {contact.email}
                        </a>
                        {contact.phone && (
                          <a href={`tel:${contact.phone}`} className="text-[#A3A3A3] text-sm hover:text-white">
                            {contact.phone}
                          </a>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => deleteContact(contact.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[#A3A3A3] text-sm">{contact.message}</p>
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <span className="text-[#666] text-xs">
                      Received: {new Date(contact.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Booking Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className={`px-4 py-2 text-sm font-medium uppercase ${getStatusColor(selectedBooking.status)}`}>
                  {selectedBooking.status}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'confirmed')}
                    disabled={isUpdating || selectedBooking.status === 'confirmed'}
                    size="sm"
                    className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                  >
                    Confirm
                  </Button>
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'completed')}
                    disabled={isUpdating || selectedBooking.status === 'completed'}
                    size="sm"
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                  >
                    Complete
                  </Button>
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                    disabled={isUpdating || selectedBooking.status === 'cancelled'}
                    size="sm"
                    className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                  >
                    Cancel
                  </Button>
                </div>
              </div>

              {/* Client Info */}
              <div className="p-4 bg-white/5 space-y-3">
                <h4 className="text-[#D4AF37] text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4" /> Client Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#A3A3A3]">Name:</span>
                    <span className="text-white ml-2">{selectedBooking.full_name}</span>
                  </div>
                  <div>
                    <span className="text-[#A3A3A3]">Company:</span>
                    <span className="text-white ml-2">{selectedBooking.company || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#A3A3A3]">Email:</span>
                    <a href={`mailto:${selectedBooking.email}`} className="text-[#D4AF37] ml-2 hover:underline">
                      {selectedBooking.email}
                    </a>
                  </div>
                  <div>
                    <span className="text-[#A3A3A3]">Phone:</span>
                    <a href={`tel:${selectedBooking.phone}`} className="text-[#D4AF37] ml-2 hover:underline">
                      {selectedBooking.phone}
                    </a>
                  </div>
                  <div>
                    <span className="text-[#A3A3A3]">Passengers:</span>
                    <span className="text-white ml-2">{selectedBooking.passengers}</span>
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              <div className="p-4 bg-white/5 space-y-3">
                <h4 className="text-[#D4AF37] text-sm font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Trip Details
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#A3A3A3]">Service Type:</span>
                    <span className="text-white">{tripTypeLabels[selectedBooking.trip_type]}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A3A3A3]">Pickup:</span>
                    <span className="text-white text-right max-w-[60%]">{selectedBooking.pickup_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A3A3A3]">Drop-off:</span>
                    <span className="text-white text-right max-w-[60%]">{selectedBooking.dropoff_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A3A3A3]">Date & Time:</span>
                    <span className="text-white">{selectedBooking.pickup_date} at {selectedBooking.pickup_time}</span>
                  </div>
                  {selectedBooking.return_trip && (
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Return:</span>
                      <span className="text-white">{selectedBooking.return_date} at {selectedBooking.return_time}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Airport Info */}
              {selectedBooking.airport && (
                <div className="p-4 bg-white/5 space-y-3">
                  <h4 className="text-[#D4AF37] text-sm font-medium">Airport Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Airport:</span>
                      <span className="text-white">{selectedBooking.airport}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Flight Number:</span>
                      <span className="text-white">{selectedBooking.flight_number || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Flight Type:</span>
                      <span className="text-white capitalize">{selectedBooking.flight_type || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Vehicle */}
              <div className="p-4 bg-white/5 space-y-3">
                <h4 className="text-[#D4AF37] text-sm font-medium flex items-center gap-2">
                  <Car className="w-4 h-4" /> Vehicle Preference
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#A3A3A3]">Vehicle:</span>
                    <span className="text-white capitalize">{selectedBooking.vehicle_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A3A3A3]">Luggage:</span>
                    <span className="text-white capitalize">{selectedBooking.luggage_amount}</span>
                  </div>
                </div>
              </div>

              {/* Special Requests */}
              {selectedBooking.special_requests && (
                <div className="p-4 bg-white/5 space-y-3">
                  <h4 className="text-[#D4AF37] text-sm font-medium">Special Requests</h4>
                  <p className="text-white text-sm">{selectedBooking.special_requests}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-[#666] text-xs">
                  Booking ID: {selectedBooking.id}
                </span>
                <Button
                  onClick={() => deleteBooking(selectedBooking.id)}
                  variant="outline"
                  className="border-red-400/30 text-red-400 hover:bg-red-400/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Booking
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
